import dayjs, { Dayjs } from 'dayjs';
import { CreateCcDataResult } from 'gcf-player/shared-utils';
import { isEmpty } from 'lodash';
import { ISO_DATE_FORMAT } from 'src/constants';
import {
  ISchedulePreviewContext,
  ISchedulePreviewWithChangeSetContext,
  LocationChannels,
} from 'src/interfaces/SchedulePreview';

import { IPreviewLayerCacheRef } from './SchedulePreviewPage.interface';

export const getMinPreviewDate = (
  earliestDefaultAssemblyStartDate?: string,
): Dayjs => {
  const last30Days = dayjs().subtract(30, 'day');

  if (!earliestDefaultAssemblyStartDate) {
    return last30Days;
  }

  const earliest = dayjs(earliestDefaultAssemblyStartDate);

  return earliest.isBefore(last30Days) ? last30Days : earliest;
};

export const getMaxPreviewDate = (): Dayjs => {
  return dayjs().add(3, 'year');
};

export const getValidDate = (
  date?: string,
  earliestDefaultAssemblyStartDate?: string,
): Dayjs => {
  if (!date || !dayjs(date, ISO_DATE_FORMAT, true).isValid()) {
    return dayjs().subtract(30, 'day');
  }

  if (!earliestDefaultAssemblyStartDate) {
    return dayjs(date, ISO_DATE_FORMAT);
  }

  const minDate = getMinPreviewDate(earliestDefaultAssemblyStartDate);
  const maxDate = getMaxPreviewDate();
  const parsedDate = dayjs(date, ISO_DATE_FORMAT);

  if (parsedDate.isBefore(minDate, 'day')) {
    return minDate;
  }

  if (parsedDate.isAfter(maxDate, 'day')) {
    return minDate;
  }

  return parsedDate;
};

export function getUniqueSortedPositions(
  selectedDate: Dayjs,
  channels?: LocationChannels,
  selectedDaypartName?: string,
): number[] {
  if (!channels || !selectedDaypartName || !selectedDate) {
    return [1];
  }

  const availableDaypartSchedules = Object.values(channels)[0]?.dayparts.find(
    (daypart) => daypart.name === selectedDaypartName,
  )?.scheduleItems;

  const allPositions = Object.values(availableDaypartSchedules?.screens ?? {})
    .flatMap((screen) => screen.sections ?? [])
    .filter((section) => {
      const start = dayjs(section.startDate);
      const end = section.endDate ? dayjs(section.endDate) : null;

      const isAfterOrSameStart =
        selectedDate.isAfter(start, 'day') || selectedDate.isSame(start, 'day');

      const isBeforeOrSameEnd =
        !end ||
        selectedDate.isBefore(end, 'day') ||
        selectedDate.isSame(end, 'day');

      return isAfterOrSameStart && isBeforeOrSameEnd;
    })
    .map((section) => section.position);

  const maxPosition = allPositions.length > 0 ? Math.max(...allPositions) : 1;

  return Array.from({ length: maxPosition }, (_, i) => i + 1);
}

export const getPreviewCacheKey = (
  selectedDaypartName?: string,
  selectedDate?: Dayjs,
): string =>
  selectedDaypartName && selectedDate
    ? `${selectedDaypartName}|${selectedDate.format(ISO_DATE_FORMAT)}`
    : '';

export const getPreviewDataFromCache = (
  cacheRef: IPreviewLayerCacheRef,
  previewSource: object | undefined,
  previewCacheKey: string,
) => {
  if (!previewCacheKey || cacheRef.current.previewSource !== previewSource) {
    return undefined;
  }

  return cacheRef.current.previews[previewCacheKey];
};

export const getPreviewLayerEntries = (
  cacheRef: IPreviewLayerCacheRef,
  previewSource: object | undefined,
  previewCacheKey: string,
  previewData?: CreateCcDataResult,
): Array<[string, CreateCcDataResult]> => {
  const previews =
    cacheRef.current.previewSource === previewSource
      ? cacheRef.current.previews
      : {};

  if (!previewCacheKey || !previewData || previews[previewCacheKey]) {
    return Object.entries(previews);
  }

  return Object.entries({
    ...previews,
    [previewCacheKey]: previewData,
  });
};

export const updatePreviewLayerCache = (
  cacheRef: IPreviewLayerCacheRef,
  previewSource: object | undefined,
  previewCacheKey: string,
  previewData?: CreateCcDataResult,
) => {
  // This cache intentionally keys off previewSource object identity rather than
  // deep equality. The source is expected to remain the same object instance
  // unless React Query gives us a new preview payload, at which point the cache
  // must be reset to avoid reusing stale preview layers.
  if (cacheRef.current.previewSource !== previewSource) {
    cacheRef.current = {
      previewSource,
      previews: {},
    };
  }

  if (previewCacheKey && previewData) {
    cacheRef.current.previews[previewCacheKey] ??= previewData;
  }
};

export const getLocationChannelsFromSchedulePreviewContext = (
  schedulePreviewContext:
    | ISchedulePreviewContext
    | ISchedulePreviewWithChangeSetContext,
  changeSetId?: string,
): LocationChannels | undefined => {
  if (changeSetId) {
    return (schedulePreviewContext as ISchedulePreviewWithChangeSetContext)
      .after?.location?.channels;
  }

  return (schedulePreviewContext as ISchedulePreviewContext).location?.channels;
};

export const getEarliestStartDateFromDefaultAssemblies = (
  locationChannels: LocationChannels | undefined,
  selectedDaypartName: string,
) => {
  if (isEmpty(locationChannels)) {
    return '';
  }

  const daypart = Object.values(locationChannels)[0].dayparts.find(
    (dayparts) => dayparts.name === selectedDaypartName,
  );

  if (!daypart) {
    return '';
  }

  const earliestStartDate = daypart.defaultAssemblies.reduce(
    (earliest, assembly) => {
      const assemblyStartDate = dayjs(assembly.startDate);

      if (!earliest) {
        return assembly.startDate;
      }

      return assemblyStartDate.isBefore(earliest, 'day')
        ? assembly.startDate
        : earliest;
    },
    '',
  );

  return earliestStartDate;
};
